import { useEffect, useMemo, useState } from 'react'
import { User } from 'types/user'
import { StatusBanner, StatusBannerResponse } from 'types/status-banner'
import { fetchCurrentUser } from 'requests/user'

const REFETCH_INTERVAL = 5 * 60 * 1000
const SERVICE_NAME = 'orders-fe'
const DISMISSED_BANNERS_KEY = 'dismissed-status-banners'

const loadCurrentUser = async (): Promise<User | null> => {
  const token = localStorage.getItem('token')

  if (!token) {
    return null
  }

  try {
    return await fetchCurrentUser(token)
  } catch {
    return null
  }
}

export const useServiceStatusBanner = () => {
  const [banners, setBanners] = useState<StatusBanner[]>([])
  const [dismissed, setDismissed] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(DISMISSED_BANNERS_KEY)

      return stored ? new Set(JSON.parse(stored)) : new Set()
    } catch {
      return new Set()
    }
  })

  const dismiss = (id: string) => {
    setDismissed((prev) => {
      const next = new Set(prev)
      next.add(id)

      localStorage.setItem(DISMISSED_BANNERS_KEY, JSON.stringify([...next]))

      return next
    })
  }

  useEffect(() => {
    let mounted = true

    const loadBanner = async () => {
      try {
        const response = await fetch('/status-banner.json', {
          cache: 'no-cache',
        })

        if (!response.ok) {
          if (mounted) {
            setBanners([])
          }

          return
        }

        const data: StatusBannerResponse = await response.json()
        const now = new Date()

        const activeBanners = data.banners
          .filter((banner) => banner.enabled)
          .filter((banner) => banner.services.includes(SERVICE_NAME))
          .filter((banner) => {
            const startAt = new Date(banner.startAt)

            if (Number.isNaN(startAt.getTime())) {
              return false
            }

            if (startAt > now) {
              return false
            }

            if (!banner.endAt) {
              return true
            }

            const endAt = new Date(banner.endAt)

            if (Number.isNaN(endAt.getTime())) {
              return false
            }

            return endAt > now
          })

        const requiresRole = activeBanners.some((banner) => !banner.roles.includes('*'))

        const user = requiresRole ? await loadCurrentUser() : null

        const visibleBanners = activeBanners
          .filter((banner) => {
            if (banner.roles.includes('*')) {
              return true
            }

            if (!user) {
              return false
            }

            return banner.roles.includes(user.role)
          })
          .sort((a, b) => b.priority - a.priority)

        if (mounted) {
          const activeIds = new Set(activeBanners.map((banner) => banner.id))

          setDismissed((prev) => {
            const next = new Set([...prev].filter((id) => activeIds.has(id)))

            localStorage.setItem(DISMISSED_BANNERS_KEY, JSON.stringify([...next]))

            return next
          })

          setBanners(visibleBanners)
        }
      } catch {
        if (mounted) {
          setBanners([])
        }
      }
    }

    loadBanner()

    const timer = window.setInterval(loadBanner, REFETCH_INTERVAL)

    return () => {
      mounted = false
      clearInterval(timer)
    }
  }, [])

  const visibleBanners = useMemo(() => banners.filter((banner) => !dismissed.has(banner.id)), [banners, dismissed])

  return {
    banners: visibleBanners,
    dismiss,
  }
}
